import { Routes, Route, Navigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import Shell from "@/layout/Shell";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import Badge from "@/components/Badge";
import EmptyState from "@/components/EmptyState";
import KpiGrid, { KpiCard } from "@/components/KpiGrid";
import { StatusDonut, TrendChart, BarChartCard } from "@/components/Charts";
import { fmtDate } from "@/lib/utils";
import { BookOpen, Building2, Image, CheckCircle2, XCircle, Clock, Search, Camera, Plus, Edit2, FileText, X, CalendarCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";


/* ── Dashboard ────────────────────────────────────────────────────── */
function Dashboard() {
  const bookings = useFetch(() => api.adminListBookings({}), []);
  const events   = useFetch(() => api.listEvents().catch(() => ({ events: [] })), []);
  const halls    = useFetch(() => api.listHalls().catch(() => ({ halls: [] })), []);
  const all      = bookings.data?.bookings || [];
  const pending    = all.filter(b => b.status === "pending");
  const confirmed  = all.filter(b => b.status === "confirmed");
  const rejected   = all.filter(b => b.status === "rejected" || b.status === "cancelled");
  const today      = all.filter(b => b.date === new Date().toISOString().slice(0,10));
  const upcoming   = [...all]
    .filter(b => b.status === "confirmed" && b.date >= new Date().toISOString().slice(0,10))
    .sort((a, b) => (a.date + (a.timeSlot || "")).localeCompare(b.date + (b.timeSlot || "")))
    .slice(0, 6);

  // Build a 14-day trendline of bookings-per-day
  const trend = useMemo(() => {
    const days = 14;
    const today0 = new Date(); today0.setHours(0, 0, 0, 0);
    const buckets = Array.from({ length: days }, (_, i) => {
      const d = new Date(today0);
      d.setDate(d.getDate() - (days - 1 - i));
      const iso = d.toISOString().slice(0, 10);
      return { key: iso, label: d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }), bookings: 0 };
    });
    const map = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
    all.forEach(b => {
      const idx = map[b.date];
      if (idx !== undefined) buckets[idx].bookings += 1;
    });
    return buckets;
  }, [all]);

  // Hall distribution bar chart
  const byHall = useMemo(() => {
    const map = new Map();
    all.forEach(b => {
      const name = b.auditorium || "Unknown";
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));
  }, [all]);

  const statusStats = {
    pending,
    confirmed,
    rejected,
    cancelled: all.filter(b => b.status === "cancelled"),
  };

  return (
    <Shell role="admin" title="Admin Dashboard" subtitle="Hall bookings overview">
      <KpiGrid items={[
        { label: "Pending",   value: pending.length,   accent: "#F59E0B", icon: Clock,        note: "Awaiting incharge approval" },
        { label: "Confirmed", value: confirmed.length, accent: "#10B981", icon: CheckCircle2, note: "Booked and on the calendar" },
        { label: "Today",     value: today.length,     accent: "#0047FF", icon: CalendarCheck, note: "Events running on the platform today" },
        { label: "Total",     value: all.length + (events.data?.events?.length || 0),
          accent: "#8B5CF6", icon: BookOpen,
          note: `Across ${(halls.data?.halls || []).length || 7} halls` },
      ]} />

      {/* Charts row */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mt-5">
        <TrendChart
          data={trend}
          dataKey="bookings"
          title="Last 14 days"
          subtitle="Bookings created"
          color="#0047FF"
        />
        <StatusDonut
          stats={Object.fromEntries(Object.entries(statusStats).map(([k, v]) => [k, v.length]))}
          title="Booking mix"
          subtitle="Where things stand"
        />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <BarChartCard
          data={byHall}
          dataKey="value"
          title="Top halls"
          subtitle="Bookings by venue"
          color="#22D3EE"
        />
        <div className="glass rounded-2xl p-5">
          <div className="overline">Next up</div>
          <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Confirmed, on the calendar</h3>
          {upcoming.length === 0 ? (
            <div className="mt-3 text-xs text-muted-foreground">Nothing confirmed yet.</div>
          ) : (
            <div className="mt-3 space-y-2">
              {upcoming.map(b => (
                <div key={b.bookingID} className="flex items-center gap-3 p-3 rounded-xl bg-black/3 dark:bg-white/3 hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                  <div className="h-9 w-9 rounded-xl bg-cobalt-500/10 text-cobalt-500 grid place-items-center shrink-0">
                    <CalendarCheck className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{b.eventName || b.bookingID}</div>
                    <div className="text-xs text-muted-foreground">{b.auditorium} · {fmtDate(b.date)} · {b.timeSlot || "—"}</div>
                  </div>
                  <Badge status="confirmed" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="surface rounded-2xl p-5 mt-5">
          <h3 className="font-bold text-[15px] mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse-dot" />
            Pending approvals ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.slice(0, 8).map(b => (
              <PendingCard key={b.bookingID} booking={b} onAction={bookings.reload} />
            ))}
          </div>
        </div>
      )}

      {today.length > 0 && (
        <div className="surface rounded-2xl p-5 mt-5">
          <h3 className="font-bold text-[15px] mb-4">Today's events</h3>
          <div className="space-y-2">
            {today.map(b => (
              <div key={b.bookingID} className="flex items-center gap-3 p-3.5 rounded-xl bg-muted/50 border border-border/50">
                <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{b.eventName || b.bookingID}</div>
                  <div className="text-xs text-muted-foreground">{b.auditorium} · {b.timeSlot}</div>
                </div>
                <Badge status={b.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

function PendingCard({ booking: b, onAction }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const act = async (status) => {
    setBusy(true);
    try { await api.adminUpdateStatus(b.bookingID, status, note); toast.success(`Booking ${status}`); onAction(); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-xl p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{b.eventName || b.bookingID}</div>
          <div className="text-xs text-muted-foreground">{b.auditorium} · {fmtDate(b.date)} · {b.timeSlot}</div>
          <div className="text-xs text-muted-foreground">{b.deptClub} · {b.name}</div>
          <div className="text-[10px] font-mono text-muted-foreground">{b.bookingID}</div>
        </div>
        {b.permissionLetter?.startsWith("http") && (
          <a href={b.permissionLetter} target="_blank" rel="noopener noreferrer" title="View letter" className="text-primary shrink-0">
            <FileText className="h-4 w-4" />
          </a>
        )}
      </div>
      <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)…" className="h-8 text-xs" />
      <div className="flex gap-2">
        <Button size="sm" className="flex-1 h-9 text-xs gap-1.5 bg-green-600 hover:bg-green-700" disabled={busy} onClick={() => act("confirmed")}>
          <CheckCircle2 className="h-3.5 w-3.5" />Approve
        </Button>
        <Button size="sm" variant="outline" className="flex-1 h-9 text-xs gap-1.5 text-destructive" disabled={busy} onClick={() => act("rejected")}>
          <XCircle className="h-3.5 w-3.5" />Reject
        </Button>
      </div>
    </div>
  );
}

/* ── All bookings ─────────────────────────────────────────────────── */
function AllBookings() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [hallFilter,   setHallFilter]   = useState("");
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");
  const [q,            setQ]            = useState("");
  const [detail,       setDetail]       = useState(null);
  const [editNote,     setEditNote]     = useState("");

  const halls    = useFetch(() => api.listHalls().then(r => r?.halls || []), []);
  const bookings = useFetch(() => api.adminListBookings({
    status: statusFilter === "all" ? undefined : statusFilter,
    hallId: hallFilter || undefined,
    from:   dateFrom   || undefined,
    to:     dateTo     || undefined,
  }), [statusFilter, hallFilter, dateFrom, dateTo]);

  const all = (bookings.data?.bookings || []).filter(b =>
    !q || [b.eventName, b.auditorium, b.bookingID, b.name, b.deptClub].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  const openDetail = (b) => { setDetail(b); setEditNote(""); };

  const updateStatus = async (newStatus) => {
    if (!detail) return;
    try { await api.adminUpdateStatus(detail.bookingID, newStatus, editNote); toast.success(`Status → ${newStatus}`); bookings.reload(); setDetail(null); }
    catch(e) { toast.error(e.message); }
  };

  return (
    <Shell role="admin" title="All bookings">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{["all","pending","confirmed","rejected","cancelled"].map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>)}</SelectContent>
        </Select>
        {(halls.data || []).length > 0 && (
          <Select value={hallFilter || "__all__"} onValueChange={v => setHallFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All halls" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All halls</SelectItem>
              {(halls.data || []).map(h => <SelectItem key={h.id} value={String(h.id)}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="w-36" />
        <Input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   placeholder="To"   className="w-36" />
      </div>

      {bookings.loading ? (
        <div className="space-y-2">{[...Array(5)].map((_,i) => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : all.length === 0 ? (
        <EmptyState icon={BookOpen} title="No bookings found" />
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/50">
                {["Booking ID","Event","Hall","Date","Dept / Name","Status",""].map(h => <th key={h} className="text-left text-xs text-muted-foreground font-medium px-4 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {all.map(b => (
                  <tr key={b.bookingID} className="border-b border-border/30 hover:bg-white/30 dark:hover:bg-white/5 cursor-pointer" onClick={() => openDetail(b)}>
                    <td className="px-4 py-2.5 font-mono text-[11px]">{b.bookingID}</td>
                    <td className="px-4 py-2.5"><div className="text-xs font-medium truncate max-w-32">{b.eventName || "—"}</div></td>
                    <td className="px-4 py-2.5 text-xs">{b.auditorium}</td>
                    <td className="px-4 py-2.5 text-xs">{fmtDate(b.date)}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground"><div>{b.deptClub}</div><div>{b.name}</div></td>
                    <td className="px-4 py-2.5"><Badge status={b.status} /></td>
                    <td className="px-4 py-2.5">
                      {b.permissionLetter?.startsWith("http") && (
                        <a href={b.permissionLetter} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} className="text-primary" title="View letter"><FileText className="h-3.5 w-3.5" /></a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Booking detail modal */}
      <Dialog open={!!detail} onOpenChange={v => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Booking detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="space-y-1 text-sm">
                {[["ID",detail.bookingID],["Hall",detail.auditorium],["Event",detail.eventName],["Date",fmtDate(detail.date)],["Time",detail.timeSlot],["Dept",detail.deptClub],["Coordinator",detail.name],["Phone",detail.phone],["Special reqs",detail.specialReqs]].map(([k,v]) => v && (
                  <div key={k} className="flex justify-between py-1 border-b border-border/20 gap-4">
                    <span className="text-muted-foreground shrink-0 text-xs">{k}</span>
                    <span className="text-xs font-medium text-right break-all">{v}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Note to requester</Label>
                <Input value={editNote} onChange={e => setEditNote(e.target.value)} placeholder="Optional note visible to the requester" />
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.status !== "confirmed"  && <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700" onClick={()=>updateStatus("confirmed")}><CheckCircle2 className="h-3.5 w-3.5"/>Approve</Button>}
                {detail.status !== "rejected"   && <Button size="sm" variant="outline" className="gap-1.5 text-destructive" onClick={()=>updateStatus("rejected")}><XCircle className="h-3.5 w-3.5"/>Reject</Button>}
                {detail.status !== "cancelled"  && <Button size="sm" variant="outline" className="gap-1.5 text-muted-foreground" onClick={()=>updateStatus("cancelled")}>Cancel</Button>}
                {detail.permissionLetter?.startsWith("http") && (
                  <a href={detail.permissionLetter} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="gap-1.5"><FileText className="h-3.5 w-3.5"/>View letter</Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

/* ── Gallery review ────────────────────────────────────────────────── */
function GalleryReview() {
  const pending = useFetch(() => api.pendingGalleries(), []);
  const events  = pending.data?.events || [];
  return (
    <Shell role="admin" title="Gallery review" subtitle="Review photographer uploads before publishing">
      {pending.loading ? (
        <div className="space-y-4">{[...Array(2)].map((_,i) => <div key={i} className="h-48 rounded-2xl shimmer" />)}</div>
      ) : events.length === 0 ? (
        <EmptyState icon={Image} title="No pending photos" description="All uploads have been reviewed." />
      ) : (
        <div className="space-y-6">
          {events.map(ev => <GalleryEventCard key={ev.bookingID} event={ev} onRefresh={pending.reload} />)}
        </div>
      )}
    </Shell>
  );
}

function GalleryEventCard({ event: ev, onRefresh }) {
  const photos = useFetch(() => api.galleryAll(ev.bookingID), [ev.bookingID]);
  const all    = photos.data?.photos || [];
  const review = async (photoID, reviewAction) => {
    try {
      await api.reviewPhoto(photoID, reviewAction, "");
      if (reviewAction === "approve") {
        // Approving publishes: makes the Drive file public and shows it on the event page
        await api.publishGallery(ev.bookingID).catch(() => {});
        toast.success("Photo approved & published"); onRefresh();
      } else {
        toast.success("Photo rejected"); photos.reload();
      }
    }
    catch(e) { toast.error(e.message); }
  };
  const publish = async () => {
    try { const r = await api.publishGallery(ev.bookingID); toast.success(r.message || "Published!"); onRefresh(); }
    catch(e) { toast.error(e.message); }
  };
  return (
    <div className="surface rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-bold text-[15px]">{ev.title || ev.bookingID}</h3><div className="text-xs text-muted-foreground">{ev.bookingID} · {fmtDate(ev.date)}</div></div>
        {all.filter(p => p.status === "approved").length > 0 && (
          <Button size="sm" onClick={publish} className="gap-1.5 text-xs"><CheckCircle2 className="h-3.5 w-3.5" />Publish approved</Button>
        )}
      </div>
      {photos.loading ? <div className="grid grid-cols-3 gap-2">{[...Array(6)].map((_,i)=><div key={i} className="aspect-square rounded-lg shimmer"/>)}</div> : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {all.map(p => (
            <div key={p.photo_id} className="relative group rounded-xl overflow-hidden aspect-square bg-muted">
              {p.public_url && p.public_url !== "pending_review" ? (
                <img src={p.public_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <a href={`https://drive.google.com/file/d/${p.drive_file_id}/view`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center h-full text-muted-foreground text-[10px] text-center p-1">
                  <Image className="h-5 w-5" />
                </a>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/60 flex items-center justify-between">
                <Badge status={p.status} className="text-[8px] py-0" />
              </div>
              {p.status === "pending" && (
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => review(p.photo_id, "approve")} className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center text-xs hover:bg-green-600"><CheckCircle2 className="h-3 w-3" /></button>
                  <button onClick={() => review(p.photo_id, "reject")}  className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"><XCircle className="h-3 w-3" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Hall configuration (super admin) ─────────────────────────────── */
function HallConfig() {
  const halls  = useFetch(() => api.saListHalls().then(r => r?.halls || []), []);
  const [editor, setEditor] = useState(null); // null = closed, {} = new, {...hall} = editing
  const [busy,   setBusy]   = useState(false);

  const openNew  = () => setEditor({ id:"", number: getNextNumber(halls.data||[]), auditoriumName:"", location:"", capacity:"", bookingPrefix:"", sheetName:"", status:"active", maintenanceNote:"", admins:[""], contacts:[{name:"",role:"",email:"",phone:""}] });
  const openEdit = h => setEditor({ ...h, admins: [...(h.admins||[]),""], contacts: [...(h.contacts||[]),{name:"",role:"",email:"",phone:""}] });

  const save = async () => {
    if (!editor.auditoriumName) { toast.error("Name required"); return; }
    if (!editor.id) { toast.error("Hall ID required"); return; }
    if (!/^[A-Za-z0-9_-]{1,32}$/.test(editor.id)) { toast.error("Hall ID: letters, digits, _ - only"); return; }
    if (!editor.capacity || isNaN(editor.capacity)) { toast.error("Capacity required"); return; }
    if (!editor.bookingPrefix || !editor.bookingPrefix.endsWith("-")) { toast.error("Booking prefix must end with '-' (e.g. BSN-)"); return; }
    setBusy(true);
    const hall = {
      id: editor.id, sheetName: editor.sheetName || editor.id,
      number: Number(editor.number), auditoriumName: editor.auditoriumName,
      location: editor.location, capacity: Number(editor.capacity),
      bookingPrefix: editor.bookingPrefix, status: editor.status,
      maintenanceNote: editor.maintenanceNote,
      admins:   (editor.admins   || []).filter(Boolean),
      contacts: (editor.contacts || []).filter(c => c.name),
    };
    try { await api.saSaveHall(hall, !editor._orig); toast.success("Hall saved"); setEditor(null); halls.reload(); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (h) => {
    const ns = h.status === "active" ? "maintenance" : "active";
    try { await api.saSetHallStatus(h.id, ns, ""); toast.success(`Status → ${ns}`); halls.reload(); }
    catch(e) { toast.error(e.message); }
  };

  const f = k => e => setEditor(p => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  return (
    <Shell role="admin" title="Hall configuration" subtitle="Create and manage auditoriums (superadmin only)"
      actions={<Button size="sm" className="gap-1.5" onClick={openNew}><Plus className="h-3.5 w-3.5" />New hall</Button>}
    >
      {halls.loading ? <div className="space-y-3">{[...Array(4)].map((_,i) => <div key={i} className="h-20 rounded-2xl shimmer" />)}</div> : (
        <div className="space-y-3">
          {(halls.data || []).map(h => (
            <div key={h.id} className="glass rounded-2xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{h.name || h.auditoriumName}</div>
                <div className="text-xs text-muted-foreground">ID: {h.id} · Prefix: {h.bookingPrefix} · {h.capacity} seats · {h.location}</div>
                {h.admins?.length > 0 && <div className="text-[10px] text-muted-foreground">Admins: {h.admins.join(", ")}</div>}
              </div>
              <Badge status={h.status || "active"} />
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEdit({ ...h, _orig: true })}><Edit2 className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => toggleStatus(h)}>{h.status === "active" ? "Maintenance" : "Activate"}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hall editor modal */}
      <Dialog open={!!editor} onOpenChange={v => !v && setEditor(null)}>
        <DialogContent className="max-w-lg glass max-h-screen overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?._orig ? "Edit hall" : "Create hall"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Hall ID * <span className="text-muted-foreground font-normal">(e.g. BSN)</span></Label>
                  <Input value={editor.id} onChange={f("id")} placeholder="BSN" className="font-mono" disabled={editor._orig} />
                </div>
                <div className="space-y-1"><Label className="text-xs">Number</Label>
                  <Input type="number" value={editor.number} onChange={f("number")} min={1} />
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Display name *</Label>
                <Input value={editor.auditoriumName} onChange={f("auditoriumName")} placeholder="BSN Auditorium" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Capacity *</Label>
                  <Input type="number" value={editor.capacity} onChange={f("capacity")} />
                </div>
                <div className="space-y-1"><Label className="text-xs">Booking prefix * <span className="text-muted-foreground font-normal">(ends with -)</span></Label>
                  <Input value={editor.bookingPrefix} onChange={f("bookingPrefix")} placeholder="BSN-" className="font-mono" />
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Location</Label>
                <Input value={editor.location} onChange={f("location")} placeholder="Block A, Ground floor" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Status</Label>
                  <select value={editor.status} onChange={f("status")} className="h-11 text-sm rounded-xl border border-input bg-white/60 dark:bg-white/5 px-3 w-full">
                    <option value="active">Active</option><option value="maintenance">Maintenance</option><option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              {editor.status !== "active" && (
                <div className="space-y-1"><Label className="text-xs">Maintenance note</Label>
                  <Input value={editor.maintenanceNote} onChange={f("maintenanceNote")} placeholder="Under repair, available from…" />
                </div>
              )}
              {/* Admins list */}
              <div className="space-y-2">
                <Label className="text-xs">Hall admin emails</Label>
                {(editor.admins || [""]).map((email, i) => (
                  <div key={i} className="flex gap-2">
                    <Input type="email" value={email} onChange={e => { const a=[...(editor.admins||[])]; a[i]=e.target.value; setEditor(p=>({...p,admins:a})); }} placeholder="admin@bmsit.in" className="text-xs flex-1" />
                    {i === (editor.admins||[]).length-1
                      ? <button onClick={() => setEditor(p=>({...p,admins:[...(p.admins||[]),""]}))} className="text-primary text-xs shrink-0">+Add</button>
                      : <button onClick={() => setEditor(p=>({...p,admins:p.admins.filter((_,j)=>j!==i)}))} className="text-destructive shrink-0"><X className="h-3.5 w-3.5" /></button>
                    }
                  </div>
                ))}
              </div>
              {/* Contacts list */}
              <div className="space-y-2">
                <Label className="text-xs">Contact persons</Label>
                {(editor.contacts || []).map((c, i) => (
                  <div key={i} className="glass rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={c.name} onChange={e=>{const cs=[...editor.contacts];cs[i]={...c,name:e.target.value};setEditor(p=>({...p,contacts:cs}));}} placeholder="Name *" className="text-xs" />
                      <Input value={c.role} onChange={e=>{const cs=[...editor.contacts];cs[i]={...c,role:e.target.value};setEditor(p=>({...p,contacts:cs}));}} placeholder="Role" className="text-xs" />
                      <Input type="email" value={c.email} onChange={e=>{const cs=[...editor.contacts];cs[i]={...c,email:e.target.value};setEditor(p=>({...p,contacts:cs}));}} placeholder="Email" className="text-xs" />
                      <Input value={c.phone} onChange={e=>{const cs=[...editor.contacts];cs[i]={...c,phone:e.target.value};setEditor(p=>({...p,contacts:cs}));}} placeholder="Phone" className="text-xs" />
                    </div>
                    <button onClick={() => setEditor(p=>({...p,contacts:p.contacts.filter((_,j)=>j!==i)}))} className="text-destructive text-[10px]">Remove</button>
                  </div>
                ))}
                <button onClick={() => setEditor(p=>({...p,contacts:[...(p.contacts||[]),{name:"",role:"",email:"",phone:""}]}))} className="text-xs text-primary hover:underline">+ Add contact</button>
              </div>
              <Button onClick={save} disabled={busy} className="w-full gap-2">{busy?"Saving…":"Save hall"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

/* ── Photographers ────────────────────────────────────────────────── */
function Photographers() {
  const list = useFetch(() => api.listPhotographers().then(r => (r?.photographers || []).filter(p => Number(p.active) !== 0)), []);
  const [email, setEmail] = useState("");
  const [name,  setName]  = useState("");
  const [busy,  setBusy]  = useState(false);
  const add = async () => {
    if (!email) return;
    setBusy(true);
    try { await api.addPhotographer(email, name); toast.success("Added"); list.reload(); setEmail(""); setName(""); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Shell role="admin" title="Photographers" subtitle="Manage who can upload event gallery photos">
      <div className="surface rounded-2xl p-5 mb-5 space-y-3">
        <p className="font-bold text-[15px] text-sm">Add photographer</p>
        <div className="flex gap-2 flex-wrap">
          <Input value={name}  onChange={e=>setName(e.target.value)}  placeholder="Name"        className="flex-1 min-w-32" />
          <Input value={email} onChange={e=>setEmail(e.target.value)} placeholder="@bmsit.in"   className="flex-1 min-w-44" type="email" />
          <Button onClick={add} disabled={busy||!email} className="gap-2 shrink-0"><Camera className="h-4 w-4" />{busy?"Adding…":"Add"}</Button>
        </div>
      </div>
      <div className="space-y-2">
        {(list.data || []).map(p => (
          <div key={p.email} className="glass rounded-xl p-3 flex items-center gap-3">
            <Camera className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <div className="flex-1 min-w-0"><div className="text-sm font-medium">{p.name || p.email}</div><div className="text-xs text-muted-foreground">{p.email}</div></div>
            <Button size="sm" variant="outline" className="h-7 text-xs text-destructive shrink-0" onClick={() => api.removePhotographer(p.email).then(() => { toast.success("Removed"); list.reload(); }).catch(e=>toast.error(e.message))}>Remove</Button>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function getNextNumber(halls) {
  const used = new Set(halls.map(h => h.number));
  for (let i = 1; i <= 99; i++) if (!used.has(i)) return i;
  return 1;
}

function SuperAdminOnly({ children }) {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/admin" replace />;
  return children;
}

export default function Admin() {
  return (
    <Routes>
      <Route index                element={<Dashboard />} />
      <Route path="bookings"      element={<AllBookings />} />
      <Route path="gallery"       element={<GalleryReview />} />
      <Route path="halls"         element={<SuperAdminOnly><HallConfig /></SuperAdminOnly>} />
      <Route path="photographers" element={<SuperAdminOnly><Photographers /></SuperAdminOnly>} />
    </Routes>
  );
}
